/**
 * Error categories for classifying token refresh failures.
 * Used to determine appropriate retry strategy.
 */
export const ErrorCategory = {
  /** Backend cold start (ECONNREFUSED, ECONNABORTED, 503, 504) - needs longer retries */
  ColdStart: 'ColdStart',

  /** Request timeout (ETIMEDOUT) - might succeed with retry */
  Timeout: 'Timeout',

  /** Network connectivity issues (NETWORK_ERROR, no response) */
  NetworkError: 'NetworkError',

  /** Permission refresh scenario (403) - ONE retry for dynamically granted permissions */
  PermissionRefresh: 'PermissionRefresh',

  /** Authentication failure (400, 401) - should logout */
  AuthFailure: 'AuthFailure',

  /** Unknown error - treat cautiously */
  Unknown: 'Unknown',
} as const;

/**
 * Error category type - union of all possible error categories.
 */
export type ErrorCategory = (typeof ErrorCategory)[keyof typeof ErrorCategory];

/**
 * Retry strategy configuration for a specific failure type.
 */
export interface RetryStrategy {
  /** Whether retry is enabled for this error type */
  enabled: boolean;

  /** Maximum number of retry attempts */
  maxAttempts: number;

  /** Delay in milliseconds before each retry attempt */
  delays: number[];

  /** Request timeout in milliseconds for this scenario */
  timeout: number;

  /** User-facing error message to display */
  userMessage: string;

  /** Whether to logout the user on failure */
  shouldLogout: boolean;

  /** Whether to show retry UI to the user */
  shouldShowRetryUI: boolean;

  /** Whether to show UI message only after first retry fails (not immediately) */
  showMessageAfterFirstRetry?: boolean;

  /** Label for telemetry/monitoring */
  telemetryLabel: string;
}

/**
 * Details about a retry attempt (for telemetry).
 */
export interface RetryAttemptDetails {
  /** Error category that triggered the retry */
  errorCategory: ErrorCategory;

  /** Current attempt number (1-based) */
  attemptNumber: number;

  /** Maximum attempts configured */
  maxAttempts: number;

  /** Delay before this attempt in milliseconds */
  delayMs: number;

  /** Telemetry label for this retry strategy */
  label: string;

  /** ISO timestamp */
  timestamp: string;
}

/**
 * Details about a successful retry (for telemetry).
 */
export interface RetrySuccessDetails {
  /** Error category that was retried */
  errorCategory: ErrorCategory;

  /** Which attempt succeeded (1-based) */
  successfulAttempt: number;

  /** Total attempts made */
  totalAttempts: number;

  /** Telemetry label */
  label: string;

  /** ISO timestamp */
  timestamp: string;
}

/**
 * Details about exhausted retry attempts (for telemetry).
 */
export interface RetryExhaustedDetails {
  /** Error category that exhausted retries */
  errorCategory: ErrorCategory;

  /** Number of attempts made */
  attemptsMade: number;

  /** Final error message */
  error: string;

  /** Telemetry label */
  label: string;

  /** ISO timestamp */
  timestamp: string;
}
